import { useMutation } from "@tanstack/react-query";
import { getPublicAuthHeader, getReturnWidgetFetchClient } from "./return-widget-client";
import type { PresignedUploadResponseDto } from "../generated/schema";

async function getPresignedUrl(file: File, shopId: string): Promise<PresignedUploadResponseDto> {
  const { data, response } = await getReturnWidgetFetchClient().POST(
    "/public/return-request/image/presigned-url",
    {
      body: { filename: file.name, shopId, mimeType: file.type, fileSize: file.size },
      params: { header: getPublicAuthHeader() },
    },
  );
  if (!response.ok) throw new Error("Failed to get presigned URL");
  return data as PresignedUploadResponseDto;
}

async function uploadToS3(url: string, fields: Record<string, string>, file: File): Promise<void> {
  const formData = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    formData.append(key, value);
  }
  formData.append("file", file);
  const response = await fetch(url, { method: "POST", body: formData });
  if (!response.ok) throw new Error("S3 upload failed");
}

async function confirmUpload(
  imageId: string,
  s3Key: string,
  file: File,
  shopId: string,
): Promise<string> {
  const { data, response } = await getReturnWidgetFetchClient().POST(
    "/public/return-request/image/confirm",
    {
      body: {
        imageId,
        s3Key,
        originalName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        shopId,
      },
      params: { header: getPublicAuthHeader() },
    },
  );
  if (!response.ok) throw new Error("Failed to confirm upload");
  return (data as { url: string }).url;
}

export async function publicUploadReturnImage(file: File, shopId: string): Promise<string> {
  const { url, fields, imageId, s3Key } = await getPresignedUrl(file, shopId);
  await uploadToS3(url, fields as Record<string, string>, file);
  return confirmUpload(imageId, s3Key, file, shopId);
}

export function usePublicUploadReturnImage(shopId: string) {
  return useMutation({ mutationFn: (file: File) => publicUploadReturnImage(file, shopId) });
}
